import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from '@invent/wl-ui-kit-next/ui';

import type { IFinding, ISnapshotUsage } from '../api/contracts';
import { isCellsField, malformedHint } from './answer-values';
import { sectionUsageCodes } from './prefill-view';
import {
  addressOf,
  answerInstanceKey,
  domAddress,
  findingText,
  gridRows,
  multiYearRows,
  type ISectionView
} from './snapshot-view';
import type { IAnswerAddress } from './use-response';

/**
 * A step's hard `step` findings stop the way on (SR-416, GA §3; the owner's Q3 (a): an alert
 * dialog).
 *
 * survey-api judges nothing about a step: its `SubmitGate` blocks submit on every hard finding,
 * whatever its `block`, and a finding's `block: step` is an authored instruction to the client —
 * "say this before the respondent leaves the step". So the decision here is the finding's own,
 * read off the state the flush landed at, and nothing is evaluated: a finding is the step's when
 * its usage is one the section draws (`sectionUsageCodes` — a table's `table_cmp` sits at the
 * table, key `''`, and a percent group's at the group). A soft `step` finding stops nothing.
 *
 * **"Save and continue" alone asks** (the survey review plan's D4); a stepper button, "Back" and
 * "Finish later" never do — the answers are saved either way, and a respondent free to move is
 * one who can fix the cause wherever it sits.
 */
export function stepBlockers(view: ISectionView, findings: readonly IFinding[]): IFinding[] {
  const codes = sectionUsageCodes(view);
  return findings.filter(
    (finding) =>
      finding.severity === 'hard' &&
      finding.block === 'step' &&
      finding.usage !== null &&
      codes.has(finding.usage)
  );
}

/**
 * The step's entries no payload can carry (the survey review plan's D8): held, never sent, so
 * survey-api has no finding about them, and "Save and continue" says them beside the step's
 * findings. `malformed` is `useResponse`'s, keyed by `addressKey`.
 *
 * Only an entry the form draws (`drawn`, `isAddressDrawn`): one in a removed row is kept — never
 * sent, and stopping nothing, since the respondent could neither see nor mend it. One in a
 * follow-up no longer drawn is gone (`useResponse`'s `dropHidden`).
 */
export function stepUnsaveable(
  view: ISectionView,
  malformed: ReadonlySet<string>,
  drawn: (address: IAnswerAddress) => boolean
): IAnswerAddress[] {
  const codes = sectionUsageCodes(view);
  return [...malformed]
    .map(addressOf)
    .filter((address) => codes.has(address.usage) && drawn(address));
}

/** Where focus goes: a finding's address, or an unsaveable entry's — `typed`, since only typed
 *  text can be unsaveable, and a table row's first control is its box, not the text. */
export interface IFocusTarget {
  usage: Nullable<string>;
  instanceId: Nullable<string>;
  instanceKey: string;
  typed?: boolean;
}

/**
 * Focuses the first control of a finding's question: its field's `[data-address]` — a scalar's
 * answer key, a container's or a table's `''` — then the first thing that takes focus inside;
 * for an unsaveable entry, the first box that takes text. A year table drawn with its years across
 * addresses a year by its column's head, which holds no box: the year's cells
 * (`td[data-row]`) are searched instead.
 */
export function focusFinding(
  finding: IFocusTarget,
  usageOf: (code: string) => ISnapshotUsage | undefined
): void {
  if (!finding.usage) return;
  const usage = usageOf(finding.usage);
  const keys = [finding.instanceKey, usage ? answerInstanceKey(usage) : '', ''];
  for (const key of keys) {
    const field = document.querySelector<HTMLElement>(
      `form [data-address="${CSS.escape(domAddress(finding.usage, finding.instanceId, key))}"]`
    );
    if (!field) continue;
    const scopes =
      field.tagName === 'TH'
        ? [
            ...(field
              .closest('table')
              ?.querySelectorAll<HTMLElement>(`td[data-row="${CSS.escape(key)}"]`) ?? [])
          ]
        : [field];
    const selector = finding.typed
      ? 'input:not([type="checkbox"]):not([type="radio"]):not([disabled]), textarea:not([disabled])'
      : 'input:not([disabled]), textarea:not([disabled]), select:not([disabled]), button:not([disabled]), [tabindex]:not([tabindex="-1"])';
    const control =
      scopes.map((scope) => scope.querySelector<HTMLElement>(selector)).find(Boolean) ?? null;
    (control ?? field).scrollIntoView({ block: 'center' });
    control?.focus();
    return;
  }
}

/** An unsaveable entry in the form's words: its question, its row, and what it wants. */
function unsaveableText(
  address: IAnswerAddress,
  usageOf: (code: string) => ISnapshotUsage | undefined,
  yearLabels: Record<string, string> | undefined
): string {
  const usage = usageOf(address.usage);
  if (!usage) return address.usage;
  let row: string | undefined;
  if (isCellsField(usage.fieldType)) {
    const rows = usage.fieldType === 'grid' ? gridRows(usage) : multiYearRows(usage, yearLabels);
    row = rows.find((candidate) => candidate.key === address.instanceKey)?.label;
  }
  return `${row ? `${usage.label}, ${row}` : usage.label} — ${malformedHint(usage.fieldType)}`;
}

/**
 * "Before you continue": the step's hard `step` findings and its unsaveable entries, one line each
 * in the form's words (`findingText`), and one way out — back to the form, focused on the first of
 * them (`onFocus`, which shows its step first when a submit met it on another). Opened by
 * "Save and continue" (`where: 'step'`), and by submit over an unsaveable entry anywhere
 * (`'survey'`). `[data-step-findings]` is the list a spec reads.
 */
export function StepFindingsDialog({
  open,
  findings,
  unsaveable,
  where,
  usageOf,
  yearLabels,
  onFocus,
  onClose
}: {
  open: boolean;
  /** Kept after the dialog shuts, so its closing still knows which field to focus. */
  findings: IFinding[];
  unsaveable: IAnswerAddress[];
  where: 'step' | 'survey';
  usageOf: (code: string) => ISnapshotUsage | undefined;
  yearLabels: Record<string, string> | undefined;
  onFocus: (target: IFocusTarget) => void;
  onClose: () => void;
}) {
  // The entry that cannot be saved first: nothing it holds has reached survey-api to be judged.
  const first: IFocusTarget | undefined = unsaveable[0]
    ? { ...unsaveable[0], typed: true }
    : findings[0];
  const one = findings.length + unsaveable.length === 1;
  return (
    <AlertDialog open={open} onOpenChange={(open) => !open && onClose()}>
      <AlertDialogContent
        onCloseAutoFocus={(event) => {
          // The press that opened it was "Save and continue" or submit; the respondent's next
          // move is the line, so focus goes there rather than back to the button.
          if (!first) return;
          event.preventDefault();
          onFocus(first);
        }}
      >
        <AlertDialogHeader>
          <AlertDialogTitle>Before you continue</AlertDialogTitle>
          <AlertDialogDescription>
            {where === 'survey'
              ? one
                ? 'One answer needs to change before you submit:'
                : 'These answers need to change before you submit:'
              : one
                ? 'One answer on this step needs to change first:'
                : 'These answers on this step need to change first:'}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <ul data-step-findings className="grid list-inside list-disc gap-1.5 text-sm">
          {unsaveable.map((address) => (
            <li
              key={`unsaveable|${address.usage}|${address.instanceId ?? ''}|${address.instanceKey}`}
            >
              {unsaveableText(address, usageOf, yearLabels)}
            </li>
          ))}
          {findings.map((finding) => (
            <li
              key={`${finding.code}|${finding.usage}|${finding.instanceId ?? ''}|${finding.instanceKey}`}
            >
              {findingText(finding, usageOf, 'panel', yearLabels)}
            </li>
          ))}
        </ul>
        <AlertDialogFooter>
          <AlertDialogAction onClick={onClose}>Back to the form</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
